import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserStorageApi, data } from '@portal/pkg-api/shared'
import { UserStorage } from '@portal/pkg-api/gen/navigator'
import { debounce } from 'lodash-es'

const GRID_VIEW_STATE_KEY = 'GRID_VIEW_STATE'
const prefix = 'shared.gridViewState'

export interface GridViewState {
  selectedPerspectiveId?: number
  [perspectiveKey: string]: any
}

// Query definitions following the established pattern
export const GridViewStateQueries = {
  gridViewState: () => ({
    queryKey: [`${prefix}.gridViewState`],
    queryFn: async (): Promise<GridViewState> => {
      try {
        const userStorage = await data(
          UserStorageApi.getUserStorageByKey(GRID_VIEW_STATE_KEY as any),
        )
        if (userStorage?.value) {
          return typeof userStorage.value === 'string'
            ? JSON.parse(userStorage.value)
            : (userStorage.value as GridViewState)
        }
        return {}
      } catch (error) {
        // If not found (404) or any other error, return empty state
        console.warn('Failed to load grid view state:', error)
        return {}
      }
    },
    retry: (failureCount: number, error: any) => {
      // Don't retry if the key doesn't exist (404)
      if (error?.response?.status === 404) {
        return false
      }
      return failureCount < 3
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  }),
}

export function useGridViewState() {
  const queryClient = useQueryClient()

  // Query for getting grid view state
  const {
    data: gridViewState = {},
    isLoading,
    error,
  } = useQuery(GridViewStateQueries.gridViewState())

  // Debounced save mutation to prevent rapid saves
  const debouncedSaveMutation = useMemo(
    () =>
      debounce(async (state: GridViewState) => {
        const userStorageData: UserStorage = {
          key: GRID_VIEW_STATE_KEY as any,
          value: JSON.stringify(state) as any,
        }
        await data(UserStorageApi.updateUserStorage(userStorageData))
      }, 300),
    [],
  )

  // Mutation for saving grid view state
  const { mutate: saveGridViewState, isPending: saveIsPending } = useMutation({
    mutationFn: async (state: GridViewState) => {
      await debouncedSaveMutation(state)
    },
    onError: (error, variables) => {
      console.error('Failed to save grid view state:', error)

      // Fallback to localStorage if UserStorage fails
      try {
        window.localStorage.setItem('gridViewState', JSON.stringify(variables))
      } catch (e) {
        console.error('Failed to save to localStorage fallback:', e)
      }
    },
  })

  // Mutation for clearing grid view state
  const { mutate: clearGridViewState, isPending: clearIsPending } = useMutation({
    mutationFn: async () => {
      await data(UserStorageApi.deleteUserStorage(GRID_VIEW_STATE_KEY as any))
    },
    onSuccess: () => {
      queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, {})
    },
    onError: (error) => {
      console.error('Failed to clear grid view state:', error)
    },
  })

  // Helper functions for specific operations
  const updatePerspectiveState = useCallback(
    (perspectiveKey: string, perspectiveState: any) => {
      const newState = {
        ...gridViewState,
        [perspectiveKey]: perspectiveState,
      }

      // Optimistically update the cache
      queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, newState)

      // Save to server
      saveGridViewState(newState)
    },
    [gridViewState, queryClient, saveGridViewState],
  )

  const setSelectedPerspectiveId = useCallback(
    (perspectiveId?: number) => {
      const newState = {
        ...gridViewState,
        selectedPerspectiveId: perspectiveId,
      }

      // Optimistically update the cache
      queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, newState)

      // Save to server
      saveGridViewState(newState)
    },
    [gridViewState, queryClient, saveGridViewState],
  )

  const clearPerspectiveState = useCallback(
    (perspectiveKey: string) => {
      const newState = { ...gridViewState }
      delete newState[perspectiveKey]

      // Optimistically update the cache
      queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, newState)

      // Save to server
      saveGridViewState(newState)
    },
    [gridViewState, queryClient, saveGridViewState],
  )

  const clearSelectedPerspectiveId = useCallback(() => {
    const newState = { ...gridViewState }
    delete newState.selectedPerspectiveId

    // Optimistically update the cache
    queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, newState)

    // Save to server
    saveGridViewState(newState)
  }, [gridViewState, queryClient, saveGridViewState])

  const clearAll = useCallback(() => {
    // Optimistically update the cache
    queryClient.setQueryData(GridViewStateQueries.gridViewState().queryKey, {})

    // Clear on server
    clearGridViewState()
  }, [queryClient, clearGridViewState])

  return useMemo(
    () => ({
      gridViewState,
      isLoading,
      error,
      updatePerspectiveState,
      setSelectedPerspectiveId,
      clearPerspectiveState,
      clearSelectedPerspectiveId,
      clearAll,
      isSaving: saveIsPending,
      isClearing: clearIsPending,
    }),
    [
      gridViewState,
      isLoading,
      error,
      updatePerspectiveState,
      setSelectedPerspectiveId,
      clearPerspectiveState,
      clearSelectedPerspectiveId,
      clearAll,
      saveIsPending,
      clearIsPending,
    ],
  )
}
